import { useEffect, useState } from "react";
import { Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

import { crmService } from "@/services/crmService";
import { FOLLOWUP_TYPES, PRIORITIES, leadTypeLabel } from "@/config/crmOptions";
import { Field } from "@/components/profile/FormControls";
import { parseFieldErrors } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__none__";

const defaultDue = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  // format for datetime-local (local time)
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Create a follow-up. Contact is always preset (from Contact Profile or a lead). */
export const AddFollowupDialog = ({ open, onOpenChange, contact, lead, leads = [], onCreated }) => {
  const [form, setForm] = useState({ due_at: "", followup_type: "call", priority: "normal", note: "", lead_id: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm({ due_at: defaultDue(), followup_type: "call", priority: "normal", note: "", lead_id: lead?.id || "" });
      setErrors({});
    }
  }, [open, lead]);

  const save = async () => {
    if (!contact?.id) return;
    setErrors({});
    setSaving(true);
    try {
      const iso = form.due_at ? new Date(form.due_at).toISOString() : "";
      await crmService.createFollowup({
        contact_id: contact.id,
        lead_id: form.lead_id || null,
        followup_type: form.followup_type,
        due_at: iso,
        priority: form.priority,
        note: form.note || null,
      });
      toast.success("Follow-up scheduled");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      setErrors(parseFieldErrors(err));
      toast.error(err.message || "Please correct the highlighted fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-followup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-accent" /> Schedule Follow-Up</DialogTitle>
          <DialogDescription>
            For {contact?.first_name} {contact?.last_name}. AgentHQ organizes your workflow — no messages are sent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Due date & time" htmlFor="fu-due" error={errors.due_at}>
            <Input id="fu-due" type="datetime-local" value={form.due_at} onChange={(e) => set("due_at", e.target.value)} data-testid="followup-due" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Type" htmlFor="fu-type">
              <Select value={form.followup_type} onValueChange={(v) => set("followup_type", v)}>
                <SelectTrigger id="fu-type" data-testid="followup-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FOLLOWUP_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Priority" htmlFor="fu-priority">
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger id="fu-priority" data-testid="followup-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          {leads.length > 0 && !lead && (
            <Field label="Link to lead (optional)" htmlFor="fu-lead">
              <Select value={form.lead_id || NONE} onValueChange={(v) => set("lead_id", v === NONE ? "" : v)}>
                <SelectTrigger id="fu-lead" data-testid="followup-lead"><SelectValue placeholder="No lead" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No lead</SelectItem>
                  {leads.map((l) => <SelectItem key={l.id} value={l.id}>{leadTypeLabel(l.lead_type)}{l.stage?.name ? ` · ${l.stage.name}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Note / context (optional)" htmlFor="fu-note" error={errors.note}>
            <Textarea id="fu-note" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="followup-note" placeholder="What's this follow-up about?" />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="add-followup-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="add-followup-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
